/**
 * AccountSlice
 * 
 * Redux slice for managing account state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, AccountSliceState } from './state';
import { buildAccountExtraReducers } from './extraReducers';

// Import all reducers
import {
  setAccount,
  clearAccount,
  updateAccount,
  restoreFromBackup,
  setLoading,
  markFieldAsEdited,
  clearEditedFields,
  markAsSaved,
  discardChanges,
  setValidationError,
  setValidationErrors,
  clearValidationError,
  clearAllValidationErrors,
  addNote,
  updateNote,
  removeNote,
} from './reducers';

/**
 * Account slice definition
 */
const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    // Core reducers
    setAccount,
    clearAccount,
    updateAccount,
    restoreFromBackup,
    setLoading,

    // Edit tracking reducers
    markFieldAsEdited,
    clearEditedFields,
    markAsSaved,
    discardChanges,

    // Validation reducers
    setValidationError,
    setValidationErrors,
    clearValidationError,
    clearAllValidationErrors,

    // Note reducers
    addNote,
    updateNote,
    removeNote,
  },
  extraReducers: buildAccountExtraReducers,
});

// Export actions
export const accountActions = accountSlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  setAccount: setAccountAction,
  clearAccount: clearAccountAction,
  updateAccount: updateAccountAction,
  restoreFromBackup: restoreFromBackupAction,
  setLoading: setLoadingAction,
  markFieldAsEdited: markFieldAsEditedAction,
  clearEditedFields: clearEditedFieldsAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  setValidationErrors: setValidationErrorsAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
  addNote: addNoteAction,
  updateNote: updateNoteAction,
  removeNote: removeNoteAction,
} = accountSlice.actions;

// Export reducer as default
export default accountSlice.reducer;

// Export state type
export type { AccountSliceState } from './state';

// Export all selectors
export * from './selectors';
